import { ShipmentByIdType } from '@Reducers/Shipment/Type';
import { RootStateType } from '@Store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  defaultBoxState,
  defaultBoxesState,
  defaultConsignee,
  defaultPackageState,
  defaultPickupState,
  defaultProductState,
  defaultShipmentTypeState,
  defaultShipperState,
} from '../../pages/CreateShipment/constants';
import { LENGTH_UNITS } from '../../pages/GetRatesV2/Constant';
import { documentWeight } from '../../pages/GetRatesV2/RatesInput/Schema';
import { UNIT, calculateWeights } from '../../services/calculateVolume';
import {
  getBoxStateObj,
  getPackageStateObj,
  getPickupStateObj,
  getShipmentTypeStateObj,
  getUserDetailsStateObj,
} from '../../services/getShipmentPayload';
import { CSPChargeType } from '../../store/reducers/GetRates/Type';
import { RateObj } from '../../store/reducers/RateSummary/Type';
import {
  BoxType,
  BulkShipmentType,
  ConsigneeType,
  ExtraDetails,
  PackageType,
  PickupDataType,
  ProductType,
  ShipmentDataType,
  ShipperType,
} from '../../types/shipment';
import CreateShipmentContext from './index';
import { useSearchQueryParams } from '@Hook/useSearchQueryParams';
import { useParams } from 'react-router-dom';
import { normalizeCpscDisclaimCode, parseBoolean, defaultCpscComplianceState } from '../../utils/usCompliance';

function CreateShipmentWrapper(props: { children: React.ReactNode }) {
  const refId = new URLSearchParams(document.location.search).get('ref');
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  // const dispatch = useDispatch<AppDispatch>();
  const { formData } = useSelector((state: RootStateType) => state.getRates);
  const { shipmentById } = useSelector((state: RootStateType) => state.shipment);
  const { singeDraftShipment, selectedDraftShipmentId: draftRefId } = useSelector(
    (state: RootStateType) => state.DraftShipment,
  );

  const [activeStep, setActiveStep] = useState<number>(0);
  const [shipper, setShipper] = useState<ShipperType>({
    ...defaultShipperState,
  });
  const orgDefaultCurrency = useSelector((state: RootStateType) => state.orgSetup?.organizationData?.currency?.name);

  const [consignee, setConsignee] = useState<ConsigneeType>({ ...defaultConsignee });
  const [packageData, setPackageData] = useState<PackageType>({ ...defaultPackageState });
  const [pickupType, setPickupType] = useState<PickupDataType>({
    ...defaultPickupState,
    date: null,
    type: name === 'Cargo Solutions Network' ? 'dropOff' : 'pickUp',
  });
  const [shipmentType, setShipmentType] = useState<ShipmentDataType>({
    ...defaultShipmentTypeState,
    currency: orgDefaultCurrency || '',
  });
  const [boxes, setBoxes] = useState<BoxType[]>(structuredClone(defaultBoxesState));
  const [selectedBox, setSelectedBox] = useState<BoxType>({ ...defaultBoxState });
  const [selectedProduct, setSelectedProduct] = useState<ProductType | null>({ ...defaultProductState });
  const [totalShipmentValue, setTotalShipmentValue] = useState<number>(0);
  const [actualWeight, setActualWeight] = useState<number>(0);
  const [chargeableWeight, setChargeableWeight] = useState<number>(0);
  const [boxValidation, setBoxValidation] = useState<boolean>(false);
  const [isProductFormInvalid, setIsProductFormInvalid] = useState<boolean>(false);
  const [isHSNValidating, setIsHSNValidating] = useState<boolean>(false);
  const [getHSNCodeFetched, setGetHSNCodeFetched] = useState<boolean>(false);
  const [showRates, setShowRates] = useState<boolean>(false);
  const [selectedCSP, setSelectedCSP] = useState<CSPChargeType | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [removeFirstBoxDeleteOption, setRemoveFirstBoxDeleteOption] = useState<boolean>(false);
  const [extraDetails, setExtraDetails] = useState<ExtraDetails>({
    purposeOfShipment: '',
    ior: '',
    iossNumber: '',
    recipientVatTaxId: '',
    incoterm: 'FOB',
    freight: '',
    freightInsurance: '',
  });
  const [dutyChargeError, setDutyChargeError] = useState<string>('');
  const [ratesSnapshot, setRatesSnapshot] = useState<string | null>(null);
  const [isBatchMode, setIsBatchMode] = useState<boolean>(false);
  const [bulkShipments, setBulkShipments] = useState<BulkShipmentType[]>([]);
  const [batchCounts, setBatchCounts] = useState<{
    pending: number;
    completed: number;
    failed: number;
    needs_review: number;
  }>({ pending: 0, completed: 0, failed: 0, needs_review: 0 });
  const [batchPickupType, setBatchPickupType] = useState<PickupDataType>({
    ...defaultPickupState,
    date: null,
    type: name === 'Cargo Solutions Network' ? 'dropOff' : 'pickUp',
  });
  const [carrierPreference, setCarrierPreference] = useState<string>('cheapest');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [rateAssignService, setRateAssignService] = useState<RateObj[]>([]);

  const resetBatchMode = () => {
    setIsBatchMode(false);
    setBulkShipments([]);
    setBatchCounts({ pending: 0, completed: 0, failed: 0, needs_review: 0 });
    setCarrierPreference('cheapest');
    setBatchId(null);
    setRateAssignService([]);
    setActiveStep(0);
  };
  const searchParams = useSearchQueryParams();
  const { sn: shipmentNumber } = useParams();

  // useEffect(() => {
  //   let totalChargeableWeight = 0;
  //   totalChargeableWeight = boxes.reduce((acc, { length, height, width, weight, products, boxCopyCount }) => {
  //     if (weight && products.length) {
  //       setBoxValidation(true);
  //     } else {
  //       setBoxValidation(false);
  //     }
  //     const unit: LengthUnitType = LENGTH_UNITS[shipmentType.unit.split('-')[0]].value;
  //     let volumetricWeight = calculateVolume(length, width, height, unit);
  //     return acc + (weight > volumetricWeight ? weight : volumetricWeight) * boxCopyCount;
  //   }, 0);
  //   setChargeableWeight(totalChargeableWeight);
  // }, [boxes, selectedBox]);

  function formatEditShipmentData(data: ShipmentByIdType) {
    setActiveStep(0);
    setShipper({ ...getUserDetailsStateObj(data._shipperId) });
    setConsignee({ ...getUserDetailsStateObj(data._consigneeId) });
    setPackageData({ ...getPackageStateObj(data._packageId, orgDefaultCurrency) });
    if (!refId) {
      setPickupType({
        ...getPickupStateObj(data._packageId),
        warehouse: data._warehouseId,
        selectedWearhouseAddress: {
          ...data?._warehouseAddressId,
          label: data?._warehouseAddressId?.label !== null ? data?._warehouseAddressId?.label : '',
        },
      });
    }
    setShipmentType({ ...getShipmentTypeStateObj(data, orgDefaultCurrency) });

    const boxDetails: BoxType[] = getBoxStateObj(data._packageId.box);

    if (boxDetails.length) {
      setBoxes([...boxDetails]);
      setSelectedBox({ ...boxDetails[0] });
      if (boxDetails[0].products?.length) {
        setSelectedProduct({ ...boxDetails[0].products[0] });
      }
    } else {
      setBoxes(structuredClone(defaultBoxesState));
      setSelectedBox({ ...defaultBoxState });
      setSelectedProduct({ ...defaultProductState });
    }

    setTotalShipmentValue(0);
    setChargeableWeight(0);
    setBoxValidation(false);
    setShowRates(false);
    setSelectedCSP(null);
    setRatesSnapshot(null);
    setNotes('');
  }

  function resetShipmentData() {
    setShipper({ ...defaultShipperState });
    setConsignee({ ...defaultConsignee });
    setPackageData({ ...defaultPackageState });
    setPickupType({ ...defaultPickupState, type: name === 'Cargo Solutions Network' ? 'dropOff' : 'pickUp' });
    setShipmentType({
      ...defaultShipmentTypeState,
      currency: orgDefaultCurrency || '',
    });
    if (!shipmentNumber) {
      setSelectedBox({ ...defaultBoxState });
      setBoxes([...defaultBoxesState]);
    }
    setSelectedProduct({ ...defaultProductState });
    setTotalShipmentValue(0);
    setChargeableWeight(0);
    setBoxValidation(false);
    setShowRates(false);
    setSelectedCSP(null);
    setRatesSnapshot(null);
    setNotes('');

    // dispatch(resetFormData());
  }
  useEffect(() => {
    if (refId && shipmentById) {
      formatEditShipmentData(shipmentById);
    } else if (!refId && !draftRefId && searchParams.redirectedFrom !== 'GetRates') {
      resetShipmentData();
    }
  }, [refId, shipmentById]);
  useEffect(() => {
    if (draftRefId && singeDraftShipment) {
      const { shipper, consignee, shipmentMode } = singeDraftShipment;
      const data = {
        _shipperId: shipper as ShipmentByIdType['_shipperId'],
        _consigneeId: consignee as ShipmentByIdType['_consigneeId'],
        _packageId: (singeDraftShipment.package || {}) as ShipmentByIdType['_packageId'],
        shipmentMode: shipmentMode || '',
        _warehouseId: singeDraftShipment?._warehouseId,
      };
      formatEditShipmentData(data as ShipmentByIdType);
    } else if (!refId && !draftRefId && searchParams.redirectedFrom !== 'GetRates') {
      resetShipmentData();
    }
  }, [draftRefId, singeDraftShipment]);

  useEffect(() => {
    if (formData?.boxes && searchParams.redirectedFrom === 'GetRates') {
      const { boxes, pickup, destination, weight, weightUnit, shipmentType: _shipmentType } = formData;
      let boxesTemp: BoxType[] = [];
      if (boxes && boxes.length) {
        boxesTemp = boxes.map((box: any, ind: number) => {
          const prods: ProductType[] = (box.products || []).map((prod: any, prodIndex: number) => {
            const rawQty = prod.quantity ?? prod.qty ?? prod.Quantity ?? prod.Qty;
            const quantity = rawQty !== undefined && rawQty !== null && rawQty !== '' ? Number(rawQty) : null;
            const rawUnitVal = prod.unitValue ?? prod.value ?? prod.UnitValue ?? prod.Value;
            const unitValue =
              rawUnitVal !== undefined && rawUnitVal !== null && rawUnitVal !== '' ? Number(rawUnitVal) : null;
            const totalVal = quantity !== null && unitValue !== null ? quantity * unitValue : 0;
            const rawCpsc = prod.cpscCompliance || prod.compliance?.us || prod.cpsc;
            const cpscCompliance = rawCpsc
              ? {
                  beingSold: parseBoolean(rawCpsc.beingSold) ?? true,
                  disclaimCode: normalizeCpscDisclaimCode(rawCpsc.disclaimCode),
                  additionalContext: rawCpsc.additionalContext || '',
                  certificateFiled: parseBoolean(rawCpsc.certificateFiled),
                  productIdRegistry: rawCpsc.productIdRegistry || '',
                  certificateVersionId: rawCpsc.certificateVersionId || '',
                  certifierId: rawCpsc.certifierId || '',
                  productId: rawCpsc.productId || '',
                  productIdType: rawCpsc.productIdType || 'SKU',
                }
              : { ...defaultCpscComplianceState };

            return {
              id: prodIndex.toString(),
              description: prod.description || prod.Description || '',
              quantity,
              unitValue,
              HSNName: prod.HSNName || prod.hsnName || '',
              HSNCode: prod.HSNCode ? String(prod.HSNCode) : prod.hsnCode ? String(prod.hsnCode) : '',
              productId: prod.productId ? String(prod.productId).trim() : '',
              productIdType: prod.productIdType ? String(prod.productIdType).trim() : 'SKU',
              merchantProductId: prod.merchantProductId ? String(prod.merchantProductId).trim() : '',
              standardManufacturerProductId: prod.standardManufacturerProductId
                ? String(prod.standardManufacturerProductId).trim()
                : '',
              nonStandardManufacturerProductId: prod.nonStandardManufacturerProductId
                ? String(prod.nonStandardManufacturerProductId).trim()
                : '',
              totalValue: totalVal,
              actions: null,
              ...(cpscCompliance ? { cpscCompliance } : {}),
            };
          });

          return {
            id: ind.toString(),
            length: box.length !== undefined && box.length !== null ? String(box.length) : '',
            width: box.width !== undefined && box.width !== null ? String(box.width) : '',
            height: box.height !== undefined && box.height !== null ? String(box.height) : '',
            weight: box.weight !== undefined && box.weight !== null ? String(box.weight) : '',
            boxCopyCount: box.boxCopyCount !== undefined && box.boxCopyCount !== null ? Number(box.boxCopyCount) : 0,
            products: prods,
            selected: ind === 0,
          };
        });
      } else {
        boxesTemp = [{ ...defaultBoxState, weight: weight !== undefined && weight !== null ? String(weight) : '' }];
      }

      if (pickup) {
        const p: any = pickup;
        setShipper(prev => ({
          ...prev,
          ...p,
          name: p.name || p.companyName || prev.name,
          companyName: p.companyName || prev.companyName,
          line1: p.line1 || prev.line1,
          line2: p.line2 || prev.line2,
          city: p.city || prev.city,
          state: p.state || prev.state,
          country: p.country || prev.country,
          pincode: p.pincode || prev.pincode,
          phoneNumber: p.phoneNumber || prev.phoneNumber,
          telephoneCode: p.telephoneCode || prev.telephoneCode,
          email: p.email || prev.email,
        }));
      }

      if (destination) {
        const d: any = destination;
        setConsignee(prev => ({
          ...prev,
          ...d,
          name: d.name || d.companyName || prev.name,
          companyName: d.companyName || prev.companyName,
          line1: d.line1 || prev.line1,
          line2: d.line2 || prev.line2,
          city: d.city || prev.city,
          state: d.state || prev.state,
          country: d.country || prev.country,
          pincode: d.pincode || prev.pincode,
          phoneNumber: d.phoneNumber || prev.phoneNumber,
          telephoneCode: d.telephoneCode || prev.telephoneCode,
          email: d.email || prev.email,
        }));
      }

      setBoxes(boxesTemp);
      setSelectedBox(boxesTemp[0]);
      if (boxesTemp[0].products && boxesTemp[0].products.length > 0) {
        setSelectedProduct(boxesTemp[0].products[0]);
      } else {
        setSelectedProduct({ ...defaultProductState });
      }

      const uom = weightUnit === 'kg' ? 'kg-cm' : 'lb-in';
      const isDoc = _shipmentType === 'document';
      const currency = (formData as any)?.currency || orgDefaultCurrency || '';
      const refNumber = (formData as any)?.userReferenceNumber || '';
      const shipmentMode = (formData as any)?.shipmentMode || '';

      setPackageData(prev => ({
        ...prev,
        currency: currency || prev.currency,
        unitOfMeasurement: uom,
        isDocument: isDoc,
        userReferenceNumber: refNumber || prev.userReferenceNumber,
      }));

      setShipmentType(prev => ({
        ...prev,
        type: _shipmentType || prev.type,
        unit: uom,
        currency: currency || prev.currency,
        referenceNumber: refNumber || prev.referenceNumber,
        shipmentMode: shipmentMode || prev.shipmentMode,
        numberOfBoxes: `${boxesTemp.length}`,
      }));
    }
  }, [formData, searchParams.redirectedFrom]);

  useEffect(() => {
    if (boxes.length) setShipmentType(prev => ({ ...prev, numberOfBoxes: `${boxes.length}` }));
  }, [boxes]);

  useEffect(() => {
    const unit: UNIT = LENGTH_UNITS[shipmentType?.unit.split('-')[0]]?.value === UNIT.cm ? UNIT.cm : UNIT.in;

    // Determine whether this is an international shipment (isShipmentExport)
    const orgCountryName = organizationData?._country?.name?.trim() || '';
    const getCountryName = (field: any): string => {
      if (!field) return '';
      if (typeof field === 'string') return field.trim();
      if (typeof field === 'object') {
        if (typeof field.country === 'string') return field.country.trim();
        if (field.country && typeof field.country === 'object' && typeof field.country.name === 'string') {
          return field.country.name.trim();
        }
      }
      return '';
    };

    const shipperCountry = getCountryName(shipper);
    const consigneeCountry = getCountryName(consignee);

    let checkCountries: string[] = [];
    if (shipperCountry || consigneeCountry) {
      checkCountries = [shipperCountry, consigneeCountry].filter(Boolean);
    } else {
      checkCountries = [getCountryName(shipmentById?._shipperId), getCountryName(shipmentById?._consigneeId)].filter(
        Boolean,
      );
    }

    const isShipmentExport = orgCountryName
      ? checkCountries.some(c => c.toLowerCase() !== orgCountryName.toLowerCase())
      : false;

    let isValid = true;

    if (isHSNValidating) {
      isValid = false;
    }

    if (isProductFormInvalid) {
      isValid = false;
    }

    for (let i = 0; i < boxes.length; i++) {
      const { products } = boxes[i];
      const weight = Number((boxes[i].weight && `${boxes[i].weight}`) || 0);
      if (weight && products?.length) {
        if (shipmentType.type === 'document' && weight > documentWeight[shipmentType.unit]) {
          isValid = false;
        } else {
          // If shipment is export, check if any product is missing HSN Code
          if (isShipmentExport) {
            const hasMissingHSN = products.some(p => !p.HSNCode?.trim());
            if (hasMissingHSN) {
              isValid = false;
            }
          }
        }
      } else {
        isValid = false;
      }
    }

    setBoxValidation(isValid);

    const { totalValue, totalActualWeight } = calculateWeights<BoxType>({
      boxes,
      unit,
    });
    setTotalShipmentValue(totalValue);
    setActualWeight(totalActualWeight);
  }, [
    boxes,
    selectedBox,
    shipmentType.type,
    shipmentType.unit,
    shipper,
    consignee,
    organizationData,
    shipmentById,
    isProductFormInvalid,
    isHSNValidating,
  ]);

  return (
    <CreateShipmentContext.Provider
      value={{
        activeStep,
        setActiveStep,
        shipper,
        setShipper,
        consignee,
        setConsignee,
        packageData,
        setPackageData,
        pickupType,
        setPickupType,
        shipmentType,
        setShipmentType,
        boxes,
        setBoxes,
        selectedBox,
        setSelectedBox,
        selectedProduct,
        setSelectedProduct,
        totalShipmentValue,
        chargeableWeight,
        setChargeableWeight,
        boxValidation,
        setBoxValidation,
        isProductFormInvalid,
        setIsProductFormInvalid,
        isHSNValidating,
        setIsHSNValidating,
        getHSNCodeFetched,
        setGetHSNCodeFetched,
        showRates,
        setShowRates,
        selectedCSP,
        setSelectedCSP,
        notes,
        setNotes,
        actualWeight,
        userId,
        setUserId,
        removeFirstBoxDeleteOption,
        setRemoveFirstBoxDeleteOption,
        extraDetails,
        setExtraDetails,
        shipmentById,
        dutyChargeError,
        setDutyChargeError,
        ratesSnapshot,
        setRatesSnapshot,
        isBatchMode,
        setIsBatchMode,
        bulkShipments,
        setBulkShipments,
        batchCounts,
        setBatchCounts,
        batchPickupType,
        setBatchPickupType,
        carrierPreference,
        setCarrierPreference,
        batchId,
        setBatchId,
        rateAssignService,
        setRateAssignService,
        resetBatchMode,
      }}
    >
      {props.children}
    </CreateShipmentContext.Provider>
  );
}

export default CreateShipmentWrapper;
